import { useMessageSource } from 'src/i18n/useMessageSource'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { Autocomplete, Box, Button, Grid2, Stack, Typography } from '@mui/material'
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined'
import { CriteriaGroupsGrid } from 'src/screens/administration/configuration-management/criteria-configuration/criteria-group/CriteriaGroupsGrid'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { Option } from 'src/shared/form/control'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Criteria_Group_Config_Bool_Exp, Criteria_Group_Config_Order_By, Query_Root } from 'src/@types/graphql'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import TextField from '@mui/material/TextField'
import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
} from 'src/shared/constants/table-settings-constants'
import { queryCriteriaGroupConfigs } from 'src/screens/administration/configuration-management/criteria-configuration/criteriaConfigQueries'
import {
  SpecificConfigFilter,
  useFilterSettingsFromLocalStorage,
  useTableSettingsFromLocalStorage,
} from 'src/screens/administration/configuration-management/ConfigurationStorageUtils'
import {
  getConfigStatusOptions,
  getOptionsForFilter,
  getTranslationForProcess,
  getTranslationForStatus,
} from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { useUserLocale } from 'src/user/UserContext'
import { getCriteriaConfigProcessOptions } from 'src/screens/administration/configuration-management/criteria-configuration/CriteriaConfigUtils'
import { SecondaryConfirmationModalDialog } from 'src/shared/modal-dialog/SecondaryConfirmationModalDialog'
import { deleteCriteriaGroup } from 'src/screens/administration/configuration-management/criteria-configuration/criteria-group/CriteriaGroupGridUtils'

const CRITERIA_GROUP_TABLE_NAME = 'CRITERIA_GROUP'

type CriteriaGroupFilters = SpecificConfigFilter<'CRITERIA_GROUP'>

const defaultCriteriaGroupTableSettings: ConfigurationTablePageSortSettings = {
  ...DEFAULT_CONFIG_TABLE_PAGE_SORT_SETTINGS,
  sortModel: [{ field: 'id', sort: 'asc' }],
}

export const CriteriaConfigurationCriteriaGroupsPage = () => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()

  // filter options
  const criteriaGroupProcessOptions: Option[] = useMemo(
    () => getOptionsForFilter(getCriteriaConfigProcessOptions(getMessage)),
    [getMessage],
  )
  const criteriaGroupStatusOptions: Option[] = useMemo(
    () => getOptionsForFilter(getConfigStatusOptions(getMessage)),
    [getMessage],
  )

  const defaultCriteriaGroupFiltersValues: CriteriaGroupFilters = useMemo(
    () => ({
      criteriaGroupProcessFilter: criteriaGroupProcessOptions[0],
      criteriaGroupStatusFilter: criteriaGroupStatusOptions[0],
    }),
    [criteriaGroupProcessOptions, criteriaGroupStatusOptions],
  )

  // grid and filter settings
  const [gridDataSettings, setGridDataSettings] = useState<ConfigurationGridDataSettings>(
    DEFAULT_CONFIG_GRID_DATA_SETTINGS,
  )
  const [tableSettings, setTableSettings] = useTableSettingsFromLocalStorage(
    CRITERIA_GROUP_TABLE_NAME,
    defaultCriteriaGroupTableSettings,
  )
  const [criteriaGroupFilters, setCriteriaGroupFilters] = useFilterSettingsFromLocalStorage(
    defaultCriteriaGroupFiltersValues,
  )

  // delete modal state
  const [deleteModalLoading, setDeleteModalLoading] = useState(false)
  const [criteriaGroupToBeDeleted, setCriteriaGroupToBeDeleted] = useState<number | null>(null)

  // table state
  const fetchRows = useCallback(async () => {
    const limit = tableSettings.paginationModel.pageSize
    const offset = tableSettings.paginationModel.page * tableSettings.paginationModel.pageSize

    const criteriaGroupIdSortValue = tableSettings.sortModel?.find((sortItem) => sortItem.field === 'id')?.sort
    const orderBy = [
      {
        id: criteriaGroupIdSortValue,
      },
      {
        code: 'asc',
      },
    ] as Array<Criteria_Group_Config_Order_By>

    const criteriaGroupConfigCondition: Criteria_Group_Config_Bool_Exp = { _and: [] }
    if (criteriaGroupFilters.criteriaGroupProcessFilter.value != null) {
      criteriaGroupConfigCondition._and?.push({
        group_processes: { _contains: criteriaGroupFilters.criteriaGroupProcessFilter.value },
      })
    }
    if (criteriaGroupFilters.criteriaGroupStatusFilter.value != null) {
      const isActive = criteriaGroupFilters.criteriaGroupStatusFilter.value == CONFIGURATION_STATUS.ACTIVE
      criteriaGroupConfigCondition._and?.push({ group_active: { _eq: isActive } })
    }

    const { data, error } = await urqlClient
      .query<
        {
          criteria_group_config: Query_Root['criteria_group_config']
          criteria_group_config_aggregate: Query_Root['criteria_group_config_aggregate']
        },
        {
          criteriaGroupConfigCondition: Criteria_Group_Config_Bool_Exp
          offset: number
          limit: number
          orderBy: Criteria_Group_Config_Order_By[]
        }
      >(queryCriteriaGroupConfigs, {
        criteriaGroupConfigCondition,
        offset,
        limit,
        orderBy,
      })
      .toPromise()

    if (error || !data?.criteria_group_config || !data?.criteria_group_config_aggregate) {
      throw new Error('Resource not found')
    }

    const fetchedRows = data.criteria_group_config.map((cgc) => ({
      id: cgc.id,
      code: cgc.code,
      groupTitle: cgc.names[locale],
      allocation: cgc.group_processes?.[0],
      status: cgc.group_active,
      is_deletable: !!cgc.criteria_group_deletability?.is_deletable,
    }))
    const fetchedRowsCount = data.criteria_group_config_aggregate.aggregate?.count ?? 0

    return {
      fetchedRows,
      fetchedRowsCount,
    }
  }, [tableSettings, criteriaGroupFilters, urqlClient, locale])

  const initRows = useCallback(() => {
    setGridDataSettings((prevGridData) => ({
      ...prevGridData,
      gridLoading: true,
    }))

    fetchRows()
      .then((res) => {
        setGridDataSettings({
          rows: res.fetchedRows,
          rowsCount: res.fetchedRowsCount,
          gridLoading: false,
        })
      })
      .catch((_) => {
        notificationService.operationFailed()
        setGridDataSettings((prevGridData) => ({
          ...prevGridData,
          gridLoading: false,
        }))
      })
  }, [fetchRows, notificationService])

  // filter handlers
  const onProcessFilterChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setCriteriaGroupFilters({
        ...criteriaGroupFilters,
        criteriaGroupProcessFilter: newValue ?? defaultCriteriaGroupFiltersValues.criteriaGroupProcessFilter,
      })
    },
    [
      tableSettings,
      criteriaGroupFilters,
      defaultCriteriaGroupFiltersValues.criteriaGroupProcessFilter,
      setCriteriaGroupFilters,
      setTableSettings,
    ],
  )

  const onStatusFilterChange = useCallback(
    (newValue: Option | null) => {
      setTableSettings({
        ...tableSettings,
        paginationModel: { ...tableSettings.paginationModel, page: 0 },
      })

      setCriteriaGroupFilters({
        ...criteriaGroupFilters,
        criteriaGroupStatusFilter: newValue ?? defaultCriteriaGroupFiltersValues.criteriaGroupStatusFilter,
      })
    },
    [
      tableSettings,
      criteriaGroupFilters,
      defaultCriteriaGroupFiltersValues.criteriaGroupStatusFilter,
      setCriteriaGroupFilters,
      setTableSettings,
    ],
  )

  const handleResetFilters = useCallback(() => {
    setCriteriaGroupFilters(defaultCriteriaGroupFiltersValues)
    setTableSettings({
      ...tableSettings,
      paginationModel: { ...tableSettings.paginationModel, page: 0 },
    })
  }, [defaultCriteriaGroupFiltersValues, tableSettings, setCriteriaGroupFilters, setTableSettings])

  // delete modal handlers
  const handleCancelDelete = useCallback(() => {
    setCriteriaGroupToBeDeleted(null)
  }, [])

  const handleDeleteConfirmed = useCallback(() => {
    if (!criteriaGroupToBeDeleted) {
      notificationService.operationFailed()
      return
    }

    setDeleteModalLoading(true)
    deleteCriteriaGroup(urqlClient)(criteriaGroupToBeDeleted)
      .then(() => {
        notificationService.deleteSuccessful()
        initRows()
      })
      .catch((_) => {
        notificationService.operationFailed()
      })
      .finally(() => {
        setDeleteModalLoading(false)
        setCriteriaGroupToBeDeleted(null)
      })
  }, [criteriaGroupToBeDeleted, initRows, notificationService, urqlClient])

  useEffect(() => {
    initRows()
  }, [initRows])

  return (
    <>
      <ScreenLayout
        title={`${getMessage('label.navigation.configuration.criteria')} ${getMessage('label.configuration')}`}
      >
        <PageLayout>
          <Stack spacing={5}>
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 1,
                padding: 2,
              }}
            >
              <Stack spacing={2}>
                <Typography variant="body1" color="text.secondary" fontWeight="bold">
                  {getMessage('label.grid.filter')}
                </Typography>
                <Grid2 container spacing={2}>
                  <Grid2 size={6}>
                    <Autocomplete
                      options={criteriaGroupProcessOptions}
                      value={criteriaGroupFilters.criteriaGroupProcessFilter}
                      onChange={(_, newVal) => onProcessFilterChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForProcess(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.process')} />}
                    />
                  </Grid2>
                  <Grid2 size={6}>
                    <Autocomplete
                      options={criteriaGroupStatusOptions}
                      value={criteriaGroupFilters.criteriaGroupStatusFilter}
                      onChange={(_, newVal) => onStatusFilterChange(newVal)}
                      getOptionLabel={(opt) => getTranslationForStatus(getMessage, opt.value)}
                      renderInput={(params) => <TextField {...params} label={getMessage('label.status')} />}
                    />
                  </Grid2>
                  <Grid2 size={4}>
                    <Button variant="text" startIcon={<FilterAltOffOutlinedIcon />} onClick={handleResetFilters}>
                      {getMessage('label.configuration.criteria.group.grid.filter.reset.button')}
                    </Button>
                  </Grid2>
                </Grid2>
              </Stack>
            </Box>
            <CriteriaGroupsGrid
              dataSettings={gridDataSettings}
              tableSettings={tableSettings}
              setTableSettings={setTableSettings}
              setCriteriaGroupToBeDeleted={setCriteriaGroupToBeDeleted}
            />
          </Stack>
        </PageLayout>
      </ScreenLayout>

      <SecondaryConfirmationModalDialog
        open={!!criteriaGroupToBeDeleted}
        onCancel={handleCancelDelete}
        onConfirm={handleDeleteConfirmed}
        loading={deleteModalLoading}
        buttonColor="error"
      >
        <Box paddingRight={1}>
          <Typography variant="subtitle1">
            {getMessage('label.configuration.criteria.group.grid.delete.confirm.body')}
          </Typography>
        </Box>
      </SecondaryConfirmationModalDialog>
    </>
  )
}
